import React, { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  Chip,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material'
import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Person from '@mui/icons-material/Person'
import VideoCall from '@mui/icons-material/VideoCall'
import ExitToApp from '@mui/icons-material/ExitToApp'
import Refresh from '@mui/icons-material/Refresh'
import Schedule from '@mui/icons-material/Schedule'
import AccessTime from '@mui/icons-material/AccessTime'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { ALL_GENDERS, EVENT_TYPES_ARRAY } from 'src/consumer/constants'
import { formatEventTimeRange } from 'src/supportPortal/components/common/bookedEventInfo/components/utils'
import { styled } from '@mui/material/styles'

dayjs.extend(utc)

// Styled Components
const TimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: '24px 0',
  minHeight: '400px',
}))

const TimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: 0,
  bottom: 0,
  width: '3px',
  background: 'linear-gradient(180deg, #4338ca 0%, #6366f1 50%, #4338ca 100%)',
  transform: 'translateX(-50%)',
  borderRadius: '2px',
}))

const TimelineEvent = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'side' && prop !== 'isOverlapping' && prop !== 'isLeaving',
})(({ theme, side, isOverlapping, isLeaving }) => ({
  position: 'relative',
  marginBottom: '16px',
  width: '45%',
  marginLeft: side === 'practitioner' ? 0 : 'auto',
  marginRight: side === 'patient' ? 0 : 'auto',
  borderRadius: '12px',
  border: `2px solid ${isLeaving ? '#dc2626' : isOverlapping ? '#f59e0b' : '#4338ca'}`,
  background: isLeaving
    ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(220, 38, 38, 0.1) 100%)'
    : isOverlapping
      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
}))

const TimelineDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'side' && prop !== 'isLeaving',
})(({ theme, side, isLeaving }) => ({
  position: 'absolute',
  left: side === 'practitioner' ? 'calc(100% + 12px)' : 'calc(-12px - 16px)',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  background: isLeaving
    ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
    : 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
  border: '3px solid #fff',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  zIndex: 2,
}))

const TimeDifferenceChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isEarly' && prop !== 'isLate',
})(({ theme, isEarly, isLate }) => ({
  fontSize: '10px',
  height: '20px',
  fontWeight: 600,
  background: isEarly
    ? 'rgba(16, 185, 129, 0.1)'
    : isLate
      ? 'rgba(220, 38, 38, 0.1)'
      : 'rgba(67, 56, 202, 0.1)',
  color: isEarly ? '#10b981' : isLate ? '#dc2626' : '#4338ca',
}))

const RefreshEventsContainer = styled(Box)(({ theme }) => ({
  marginTop: '24px',
  padding: '16px',
  background: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
}))

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  maxHeight: '100%',
  overflowY: 'auto',
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: '20px 24px',
  borderBottom: '1px solid rgba(67, 56, 202, 0.1)',
  background: 'rgba(255, 255, 255, 0.95)',
}))

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: 'rgba(67, 56, 202, 0.9)',
  marginBottom: '16px',
}))

const HeaderInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  '& > *': {
    flex: '0 0 auto',
  },
}))

export default function BookedEventLogsListing({
  bookedEventInfo,
  closeDialog,
  apiEndpoint = '/api/secure/cc/support/events/logs/get',
}) {
  const currentMentorName = bookedEventInfo?.mentorName
  const currentPatientName = bookedEventInfo?.patientInfo?.name
  const currentPatientNumber = bookedEventInfo?.patientInfo?.patientNumber
  const [bookedEventLogs, setBookedEventLogs] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const scheduledStartTime = useMemo(() => {
    return bookedEventInfo?.startTime ? dayjs.utc(bookedEventInfo.startTime).local() : null
  }, [bookedEventInfo?.startTime])

  async function getBookedEventLogs() {
    setLoading(true)
    const resp = await getSPDataWithParams(apiEndpoint, {
      'booked-event-id': bookedEventInfo?.id,
      page: 0,
      pageSize: 500,
      sort: 'createdTime,asc',
    })
    if (resp?.status === 200) {
      setBookedEventLogs(resp?.data.content)
    }
    setLoading(false)
  }

  React.useEffect(() => {
    if (bookedEventInfo?.id) {
      getBookedEventLogs()
    }
  }, [bookedEventInfo?.id])

  // Separate timeline events from checkin/refresh events
  const { timelineEvents, refreshEvents } = useMemo(() => {
    const timeline = []
    const refresh = []

    bookedEventLogs?.forEach((log) => {
      const action = log?.action?.toUpperCase()
      const isRefreshOrCheckin = action === 'REFRESH' || action === 'CHECKIN'

      if (isRefreshOrCheckin) {
        refresh.push(log)
      } else {
        timeline.push(log)
      }
    })

    return { timelineEvents: timeline, refreshEvents: refresh }
  }, [bookedEventLogs])

  // Process timeline events with time calculations
  const processedTimelineEvents = useMemo(() => {
    if (!scheduledStartTime) return []

    const practitionerEvents = []
    const patientEvents = []
    let lastPractitionerEvent = null
    let lastPatientEvent = null

    timelineEvents.forEach((log) => {
      const eventTime = dayjs.utc(log.createdTime).local()
      const role = log.role === 'endUser' ? 'patient' : 'practitioner'
      const action = log?.action?.toUpperCase()

      // Check if this is a leaving-type event
      const isLeaving =
        action === 'LEAVE' ||
        action === 'THROWN_OUT' ||
        action === 'FOUND_ABSENT' ||
        action === 'MARKED_STALE'

      // Calculate time difference from scheduled start
      const diffFromScheduled = eventTime.diff(scheduledStartTime, 'seconds')
      const isEarlyFromScheduled = diffFromScheduled < 0
      const isLateFromScheduled = diffFromScheduled > 0

      // Calculate time difference from previous event of same role
      const lastEvent = role === 'practitioner' ? lastPractitionerEvent : lastPatientEvent
      let diffFromPrevious = null
      let isEarlyFromPrevious = false
      let isLateFromPrevious = false

      if (lastEvent) {
        const lastEventTime = dayjs.utc(lastEvent.createdTime).local()
        diffFromPrevious = eventTime.diff(lastEventTime, 'seconds')
        isEarlyFromPrevious = diffFromPrevious < 0
        isLateFromPrevious = diffFromPrevious > 0
      }

      // Check for overlapping time with opposite role's last event
      let isOverlapping = false
      if (role === 'practitioner' && lastPatientEvent) {
        const lastPatientTime = dayjs.utc(lastPatientEvent.createdTime).local()
        const timeDiff = Math.abs(eventTime.diff(lastPatientTime, 'seconds'))
        isOverlapping = timeDiff < 30 // Consider overlapping if within 30 seconds
      } else if (role === 'patient' && lastPractitionerEvent) {
        const lastPractitionerTime = dayjs.utc(lastPractitionerEvent.createdTime).local()
        const timeDiff = Math.abs(eventTime.diff(lastPractitionerTime, 'seconds'))
        isOverlapping = timeDiff < 30
      }

      const processedEvent = {
        ...log,
        eventTime,
        role,
        action,
        isLeaving,
        diffFromScheduled,
        isEarlyFromScheduled,
        isLateFromScheduled,
        diffFromPrevious,
        isEarlyFromPrevious,
        isLateFromPrevious,
        isOverlapping,
      }

      if (role === 'practitioner') {
        practitionerEvents.push(processedEvent)
        lastPractitionerEvent = processedEvent
      } else {
        patientEvents.push(processedEvent)
        lastPatientEvent = processedEvent
      }
    })

    // Merge and sort all events by time
    const allEvents = [...practitionerEvents, ...patientEvents].sort((a, b) =>
      a.eventTime.isBefore(b.eventTime) ? -1 : 1,
    )

    return allEvents
  }, [timelineEvents, scheduledStartTime])

  const getActionLabel = (action) => {
    switch (action) {
      case 'JOIN':
        return 'Joined'
      case 'LEAVE':
        return 'Left'
      case 'JOIN_AGORA_CALL':
        return 'Joined Video Call'
      case 'LEAVE_AGORA_CALL':
        return 'Left Video Call'
      case 'THROWN_OUT':
        return 'Thrown Out'
      case 'FOUND_ABSENT':
        return 'Found Absent'
      case 'MARKED_STALE':
        return 'Marked Stale'
      default:
        return action
    }
  }

  const getActionIcon = (action) => {
    switch (action) {
      case 'JOIN':
      case 'JOIN_AGORA_CALL':
        return <CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />
      case 'LEAVE':
      case 'LEAVE_AGORA_CALL':
      case 'THROWN_OUT':
      case 'FOUND_ABSENT':
      case 'MARKED_STALE':
        return <ExitToApp sx={{ fontSize: 18, color: '#dc2626' }} />
      default:
        return <Schedule sx={{ fontSize: 18 }} />
    }
  }

  const formatTimeDifference = (seconds) => {
    const absSeconds = Math.abs(seconds)
    if (absSeconds < 60) {
      return `${absSeconds}s`
    } else if (absSeconds < 3600) {
      const mins = Math.floor(absSeconds / 60)
      const secs = absSeconds % 60
      return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
    } else {
      const hours = Math.floor(absSeconds / 3600)
      const mins = Math.floor((absSeconds % 3600) / 60)
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
  }

  const currentEventType = EVENT_TYPES_ARRAY?.find((event) => {
    return event?.id === bookedEventInfo?.eventType
  })

  return (
    <>
      <HeaderSection>
        <HeaderTitle>Join/Leave Logs</HeaderTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', marginBottom: '16px' }}>
          <Chip
            icon={<AccessTime sx={{ fontSize: 16 }} />}
            label={formatEventTimeRange(bookedEventInfo)}
            size="small"
            sx={{
              background: 'rgba(67, 56, 202, 0.1)',
              color: '#4338ca',
              fontWeight: 500,
            }}
          />
          {currentEventType?.title && (
            <Chip
              icon={<VideoCall sx={{ fontSize: 16 }} />}
              label={currentEventType?.title}
              size="small"
              sx={{
                background: 'rgba(99, 102, 241, 0.1)',
                color: '#6366f1',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
        <HeaderInfo>
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 600 }}>
              Practitioner
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#4338ca' }}>
              {bookedEventInfo?.mentorName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 600 }}>
              Patient
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {bookedEventInfo?.patientInfo?.name} ({bookedEventInfo?.patientInfo?.patientNumber})
            </Typography>
          </Box>
          {scheduledStartTime && (
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 600 }}>
                Scheduled Start
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {scheduledStartTime.format('DD MMM YYYY, hh:mm:ss A')}
              </Typography>
            </Box>
          )}
        </HeaderInfo>
      </HeaderSection>

      <ContentContainer>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {processedTimelineEvents.length > 0 ? (
              <TimelineContainer>
                <TimelineLine />
                {processedTimelineEvents.map((event, index) => {
                  const side = event.role === 'practitioner' ? 'practitioner' : 'patient'
                  const actorName = side === 'practitioner' ? currentMentorName : currentPatientName

                  return (
                    <TimelineEvent key={event.id || index} side={side} isOverlapping={event.isOverlapping} isLeaving={event.isLeaving}>
                      <TimelineDot side={side} isLeaving={event.isLeaving} />
                      <CardContent sx={{ padding: '12px 16px !important' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '8px' }}>
                          {getActionIcon(event.action)}
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                            {getActionLabel(event.action)}
                          </Typography>
                          {event.isOverlapping && (
                            <Chip
                              label="Overlapping"
                              size="small"
                              sx={{
                                fontSize: '9px',
                                height: '18px',
                                background: 'rgba(245, 158, 11, 0.1)',
                                color: '#f59e0b',
                                fontWeight: 600,
                              }}
                            />
                          )}
                          {event.isLeaving && (
                            <Chip
                              label="Session End"
                              size="small"
                              sx={{
                                fontSize: '9px',
                                height: '18px',
                                background: 'rgba(220, 38, 38, 0.1)',
                                color: '#dc2626',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>

                        <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)', marginBottom: '8px' }}>
                          {actorName} ({side === 'practitioner' ? 'Practitioner' : 'Patient'})
                        </Typography>

                        <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginBottom: '8px' }}>
                          {event.eventTime.format('DD MMM YYYY, hh:mm:ss A')}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {scheduledStartTime && (
                            <Tooltip title={`Time difference from scheduled start time`}>
                              <TimeDifferenceChip
                                icon={<Schedule sx={{ fontSize: 12 }} />}
                                label={`${event.isEarlyFromScheduled ? 'Early' : event.isLateFromScheduled ? 'Late' : 'On Time'}: ${formatTimeDifference(event.diffFromScheduled)} ${event.isEarlyFromScheduled ? 'before' : 'after'} scheduled`}
                                size="small"
                                isEarly={event.isEarlyFromScheduled}
                                isLate={event.isLateFromScheduled}
                              />
                            </Tooltip>
                          )}

                          {event.diffFromPrevious !== null && (
                            <Tooltip title={`Time difference from previous ${side} event`}>
                              <TimeDifferenceChip
                                icon={<AccessTime sx={{ fontSize: 12 }} />}
                                label={`${event.isEarlyFromPrevious ? 'Early' : event.isLateFromPrevious ? 'Late' : 'Same'} by ${formatTimeDifference(event.diffFromPrevious)} from previous`}
                                size="small"
                                isEarly={event.isEarlyFromPrevious}
                                isLate={event.isLateFromPrevious}
                              />
                            </Tooltip>
                          )}
                        </Box>

                        {event.clientIOState && (
                          <Box sx={{ display: 'flex', gap: 1, marginTop: '8px' }}>
                            <Chip
                              size="small"
                              label={`Camera ${event.clientIOState?.camera ? 'ON' : 'OFF'}`}
                              color={event.clientIOState?.camera ? 'success' : 'error'}
                              icon={event.clientIOState?.camera ? <CheckCircle /> : <Cancel />}
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                            <Chip
                              size="small"
                              label={`Mic ${event.clientIOState?.mic ? 'ON' : 'OFF'}`}
                              color={event.clientIOState?.mic ? 'success' : 'error'}
                              icon={event.clientIOState?.mic ? <CheckCircle /> : <Cancel />}
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </TimelineEvent>
                  )
                })}
              </TimelineContainer>
            ) : (
              <Box sx={{ textAlign: 'center', padding: '40px', color: 'rgba(0, 0, 0, 0.5)' }}>
                <Typography variant="body1">No timeline events found</Typography>
              </Box>
            )}

            {refreshEvents.length > 0 && (
              <RefreshEventsContainer>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '12px' }}>
                  <Refresh sx={{ fontSize: 20, color: '#6366f1' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Check-in & Refresh Events
                  </Typography>
                  <Chip label={refreshEvents.length} size="small" sx={{ marginLeft: 'auto' }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {refreshEvents.map((event, index) => {
                    const eventTime = dayjs.utc(event.createdTime).local()
                    const actorName = event.role === 'endUser' ? currentPatientName : currentMentorName
                    const role = event.role === 'endUser' ? 'Patient' : 'Practitioner'

                    return (
                      <Box
                        key={event.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          padding: '8px 12px',
                          background: '#fff',
                          borderRadius: '8px',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                        }}
                      >
                        <Refresh sx={{ fontSize: 16, color: '#6366f1' }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {actorName} ({role}) - {event.action === 'REFRESH' ? 'Refresh' : 'Check-in'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                            {eventTime.format('DD MMM YYYY, hh:mm:ss A')}
                          </Typography>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </RefreshEventsContainer>
            )}
          </>
        )}
      </ContentContainer>
    </>
  )
}
